/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { Customer } from '@msdyn365-commerce/retail-proxy';

/**
 * Input for getUserDataAndAddresses action.
 */
export class GetUserDataAndAddressesInput implements Msdyn365.IActionInput {
    public apiSettings: any;
    public B2BUserId?: string;

    constructor(apiSettings: any, B2BUserId?: string) {
        this.apiSettings = apiSettings;
        this.B2BUserId = B2BUserId;
    }

    public getCacheKey = () => `GetUserDataAndAddresses_${this.B2BUserId ?? 'current'}`;
    public getCacheObjectType = () => 'GetUserDataAndAddresses';
    public dataCacheType = (): Msdyn365.CacheType => 'none';
}

export const createInput = (inputData: Msdyn365.ICreateActionContext & { B2BUserId?: string }): Msdyn365.IActionInput => {
    const { requestContext, B2BUserId } = inputData;

    // Check if the user is authenticated.
    if (!requestContext.user.isAuthenticated) {
        throw new Error('User is not logged in.');
    }

    return new GetUserDataAndAddressesInput(requestContext.apiSettings, B2BUserId);
};

/**
 * Interface for address with LocationId.
 */
export interface IAddressWithLocation {
    RecordId?: number;
    Name?: string;
    Street?: string;
    City?: string;
    State?: string;
    ZipCode?: string;
    ThreeLetterISORegionName?: string;
    LocationId?: string;
    [key: string]: Msdyn365.IAny;
}

/**
 * Response from getUserDataAndAddresses action.
 */
export interface IGetUserDataAndAddressesResponse {
    customer?: Customer;
    addresses?: IAddressWithLocation[];
    Addresses?: IAddressWithLocation[];
    B2BUserId?: string;
}

/**
 * Action to get user data and addresses with LocationId.
 */
async function getUserDataAndAddressesAction(
    _input: GetUserDataAndAddressesInput,
    ctx: Msdyn365.IActionContext
): Promise<IGetUserDataAndAddressesResponse> {
    // Additional diagnostics: log requestContext keys and a stack trace so we can identify callers

    // Determine API URL - prefer explicit B2B user id if provided in input
    const apiSettings = ctx.requestContext.apiSettings;
    const userToken = ctx.requestContext.user.token;

    // If input has a B2BUserId prefer that
    const inputB2BUserId = _input.B2BUserId;
    let url: string;
    let usedB2BUserId: string | undefined;
    if (inputB2BUserId) {
        usedB2BUserId = inputB2BUserId;
        // Use the B2BUserId parameter when B2BUserId is provided
        url = `${apiSettings.baseUrl}Commerce/GetUserDataAndAddresses(B2BUserId='${inputB2BUserId}')`;
    } else {
        // No B2BUserId provided - call the endpoint without B2BUserId parameter
        url = `${apiSettings.baseUrl}Commerce/GetUserDataAndAddresses()`;
    }

    try {
        const headers: Record<string, string> = {
            oun: apiSettings.oun,
            'Content-Type': 'application/json'
        };
        if (userToken) {
            headers.Authorization = `id_token ${userToken}`;
        }

        const response = await Msdyn365.sendRequest<IGetUserDataAndAddressesResponse>(url, 'get', undefined, {
            headers
        });

        const addressesData = response.data?.Addresses || [];

        return {
            customer: response.data?.customer,
            addresses: addressesData,
            B2BUserId: usedB2BUserId
        };
    } catch (error) {
        ctx.telemetry.error('Failed to get user data and addresses', error);
        throw error;
    }
}

export default Msdyn365.createObservableDataAction({
    id: 'GetUserDataAndAddresses',
    action: <Msdyn365.IAction<IGetUserDataAndAddressesResponse>>getUserDataAndAddressesAction,
    input: createInput
});
